import { useState } from "react";
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from "@stripe/react-stripe-js";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Sparkles } from "lucide-react";
import { getStripe, getStripeEnvironment } from "@/lib/stripe";
import { supabase } from "@/integrations/supabase/client";

type Pack = {
  priceId: string;
  credits: number;
  priceLabel: string;
  perCreditHint: string;
  highlight?: boolean;
};

const PACKS: Pack[] = [
  {
    priceId: "ai_topup_small_v1",
    credits: 130,
    priceLabel: "$5",
    perCreditHint: "≈ 18 brief drafts",
  },
  {
    priceId: "ai_topup_medium_v1",
    credits: 280,
    priceLabel: "$10",
    perCreditHint: "≈ 40 brief drafts",
    highlight: true,
  },
  {
    priceId: "ai_topup_large_v1",
    credits: 720,
    priceLabel: "$25",
    perCreditHint: "≈ 100 brief drafts",
  },
];

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string;
};

export const BuyCreditsDialog = ({ open, onOpenChange, workspaceId }: Props) => {
  const [selected, setSelected] = useState<Pack | null>(null);

  const fetchClientSecret = async (): Promise<string> => {
    if (!selected) throw new Error("No pack selected");
    const { data, error } = await supabase.functions.invoke("ai-topup-checkout", {
      body: {
        priceId: selected.priceId,
        workspaceId,
        environment: getStripeEnvironment(),
        returnUrl: `${window.location.origin}/settings?topup=success&session_id={CHECKOUT_SESSION_ID}`,
      },
    });
    if (error || !data?.clientSecret) {
      throw new Error(error?.message || "Failed to start checkout");
    }
    return data.clientSecret as string;
  };

  const handleClose = (next: boolean) => {
    if (!next) setSelected(null);
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl">
        {selected ? (
          <>
            <DialogHeader>
              <Button
                variant="ghost"
                size="sm"
                className="self-start -ml-2 mb-2"
                onClick={() => setSelected(null)}
              >
                <ArrowLeft className="h-4 w-4 mr-1" />
                Back to packs
              </Button>
              <DialogTitle>Buy {selected.credits} credits — {selected.priceLabel}</DialogTitle>
              <DialogDescription>
                Credits never expire and are used after your monthly allowance is spent.
              </DialogDescription>
            </DialogHeader>
            <div className="min-h-[500px]">
              <EmbeddedCheckoutProvider
                stripe={getStripe()}
                options={{ fetchClientSecret }}
              >
                <EmbeddedCheckout />
              </EmbeddedCheckoutProvider>
            </div>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Buy AI credits</DialogTitle>
              <DialogDescription>
                Top-ups roll over month to month and are used after your monthly
                allowance runs out. One-time purchase — no subscription.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-3 sm:grid-cols-3 mt-2">
              {PACKS.map((pack) => (
                <Card
                  key={pack.priceId}
                  className={`relative cursor-pointer transition-all hover:border-primary hover:shadow-md ${
                    pack.highlight ? "border-primary" : ""
                  }`}
                  onClick={() => setSelected(pack)}
                >
                  {pack.highlight && (
                    <div className="absolute -top-2 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded-full bg-primary text-primary-foreground text-[10px] font-medium uppercase tracking-wide flex items-center gap-1">
                      <Sparkles className="h-2.5 w-2.5" />
                      Best value
                    </div>
                  )}
                  <CardContent className="p-4 space-y-2 text-center">
                    <div className="text-2xl font-semibold">{pack.credits}</div>
                    <div className="text-xs text-muted-foreground">credits</div>
                    <div className="text-lg font-medium">{pack.priceLabel}</div>
                    <p className="text-[11px] text-muted-foreground leading-tight">
                      {pack.perCreditHint}
                    </p>
                    <Button size="sm" className="w-full mt-2" variant={pack.highlight ? "default" : "outline"}>
                      Choose
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
            <p className="text-[11px] text-muted-foreground text-center mt-2">
              Brief drafting uses ~7 credits. Plan and refine use 1–2 credits each.
            </p>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
